import { type ChangeEvent, type ReactElement, useId } from 'react';
import { Input, Label } from '@/shared/ui';

interface LandingHeroCopyFieldProps {
  readonly label: string;
  /** 스크린리더용 이름 — 카드가 여럿이라 순번을 붙인다(예: "1번 배너 큰 문구"). */
  readonly accessibleName: string;
  readonly value: string;
  readonly maxLength: number;
  readonly placeholder: string;
  readonly error: string | undefined;
  readonly disabled: boolean;
  readonly onChange: (value: string) => void;
}

/** 배너 문구 입력 칸 하나 — 라벨·글자 수·입력·오류. */
export const LandingHeroCopyField = ({
  label,
  accessibleName,
  value,
  maxLength,
  placeholder,
  error,
  disabled,
  onChange,
}: LandingHeroCopyFieldProps): ReactElement => {
  const id = useId();
  const errorId = `${id}-error`;
  const counterId = `${id}-counter`;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id} className="text-xs">
          {label}
        </Label>
        <span id={counterId} className="text-[11px] text-muted-foreground tabular-nums">
          {value.length}/{maxLength}
        </span>
      </div>
      <Input
        id={id}
        aria-label={accessibleName}
        aria-describedby={error === undefined ? counterId : `${errorId} ${counterId}`}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        hasError={error !== undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        className="h-8 text-[13px]"
      />
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
